// Smart Sash Admin Panel - Permission Hook
// Provides permission-based access checking from auth context

import { Action, actionToPermission, routePermissions } from '@/config/permissions';
import { UserRole } from '@/models';
import { useAuth } from '@/services/auth';
import { useMemo } from 'react';

// Pre-built regex entries for dynamic route patterns (e.g. /users/:userId/scopes)
const dynamicRoutePatterns = Object.keys(routePermissions)
  .filter((p) => p.includes(':'))
  .map((pattern) => ({
    pattern,
    regex: new RegExp(`^${pattern.replace(/:[^/]+/g, '[^/]+')}$`),
  }));

function resolveRoutePattern(path: string): string {
  if (path in routePermissions) return path;
  return dynamicRoutePatterns.find(({ regex }) => regex.test(path))?.pattern ?? path;
}

export interface UsePermissionsReturn {
  /** Current user's roles */
  roles: UserRole[];
  /** Current user's resolved permissions */
  permissions: string[];
  /** Check if user has at least one of the specified roles */
  hasRole: (...roles: UserRole[]) => boolean;
  /** Check if user has at least one of the specified permission keys */
  hasPermission: (...keys: string[]) => boolean;
  /** Check if user can access a route path */
  canAccessRoute: (path: string) => boolean;
  /** Check if user can perform an action */
  canPerform: (action: Action) => boolean;
  /** True if user is HawkinsAdmin */
  isHawkinsAdmin: boolean;
  /** True if user has any admin role (HawkinsAdmin, InstitutionAdmin, or LabAdmin) */
  isAdmin: boolean;
}

export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();

  return useMemo(() => {
    const roles = user?.roles ?? [];
    const permissions = user?.permissions ?? [];
    const roleSet = new Set(roles);
    const permSet = new Set(permissions);

    const isFullAccess = permSet.has('FullAccess');
    const isHawkinsAdmin = roleSet.has(UserRole.HawkinsAdmin);

    const hasRole = (...checkRoles: UserRole[]): boolean =>
      isHawkinsAdmin || checkRoles.some((r) => roleSet.has(r));

    const hasPermission = (...keys: string[]): boolean =>
      isFullAccess || keys.some((k) => permSet.has(k));

    const canAccessRoute = (path: string): boolean => {
      if (isFullAccess) return true;
      const resolved = resolveRoutePattern(path);
      const required = routePermissions[resolved];
      if (!required || required.length === 0) return true;
      return required.some((p) => permSet.has(p));
    };

    const canPerform = (action: Action): boolean => {
      if (isFullAccess) return true;
      const permKey = actionToPermission[action];
      if (!permKey) return false;
      return permSet.has(permKey);
    };

    const isAdmin = hasRole(UserRole.HawkinsAdmin, UserRole.InstitutionAdmin, UserRole.LabAdmin);

    return {
      roles,
      permissions,
      hasRole,
      hasPermission,
      canAccessRoute,
      canPerform,
      isHawkinsAdmin,
      isAdmin,
    };
  }, [user?.roles, user?.permissions]);
}
